import React, { useState, useEffect } from 'react';
import {
  Container,
  Header,
  Table,
  Label,
  Icon,
  Segment,
  Loader,
  Accordion,
} from 'semantic-ui-react';
import { fetchApi, getApiPath } from '../../api';

const LANGUAGE_NAMES = {
  af: 'Afrikaans', ar: 'Arabic', bg: 'Bulgarian', bn: 'Bengali',
  ca: 'Catalan', cs: 'Czech', cy: 'Welsh', da: 'Danish',
  de: 'German', el: 'Greek', en: 'English', 'en-AU': 'English (AU)',
  'en-GB': 'English (GB)', eo: 'Esperanto', es: 'Spanish',
  et: 'Estonian', eu: 'Basque', fa: 'Persian', fi: 'Finnish',
  fr: 'French', fu: 'Friulian', ga: 'Irish', gl: 'Galician',
  he: 'Hebrew', hi: 'Hindi', hr: 'Croatian', hu: 'Hungarian',
  hy: 'Armenian', id: 'Indonesian', it: 'Italian', ja: 'Japanese',
  ka: 'Georgian', kn: 'Kannada', ko: 'Korean', lt: 'Lithuanian',
  lv: 'Latvian', mi: 'Maori', 'mk-MK': 'Macedonian', mt: 'Maltese',
  my: 'Burmese', nl: 'Dutch', 'nl-BE': 'Flemish', nn: 'Norwegian Nynorsk',
  no: 'Norwegian', pl: 'Polish', pt: 'Portuguese', 'pt-BR': 'Portuguese (BR)',
  rm: 'Romansh', ro: 'Romanian', ru: 'Russian', sk: 'Slovak',
  sl: 'Slovenian', sm: 'Samoan', sq: 'Albanian', sr: 'Serbian',
  'sr-Cyrl': 'Serbian (Cyrillic)', 'sr-Latn': 'Serbian (Latin)',
  sv: 'Swedish', te: 'Telugu', th: 'Thai', tl: 'Tagalog',
  to: 'Tongan', tr: 'Turkish', uk: 'Ukrainian', vi: 'Vietnamese',
  'zh-CN': 'Chinese (Simplified)', 'zh-HK': 'Chinese (HK)',
  'zh-TW': 'Chinese (Traditional)',
};

function langName(code) {
  return LANGUAGE_NAMES[code] || code;
}

function formatNumber(n) {
  return n.toLocaleString();
}

const ContributorShowcase = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});

  useEffect(() => {
    fetchApi(`${getApiPath()}/@polyglot/contributors/showcase`)
      .then((json) => {
        setData(json);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message || String(err));
        setLoading(false);
      });
  }, []);

  const toggleRow = (id) => {
    setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  if (loading) {
    return (
      <Container style={{ padding: '2em 0' }}>
        <Loader active inline="centered">Loading contributors...</Loader>
      </Container>
    );
  }

  if (error) {
    return (
      <Container style={{ padding: '2em 0' }}>
        <Segment color="red">Error: {error}</Segment>
      </Container>
    );
  }

  const items = data?.items || [];

  return (
    <Container style={{ padding: '2em 0' }}>
      <Header as="h1">
        <Icon name="users" />
        <Header.Content>
          Translation Contributors
          <Header.Subheader>
            {items.length} contributor{items.length !== 1 ? 's' : ''} working across{' '}
            {new Set(items.flatMap((c) => (c.contributions || []).map((l) => l.language))).size}{' '}
            languages
          </Header.Subheader>
        </Header.Content>
      </Header>

      <Table celled striped selectable>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>Contributor</Table.HeaderCell>
            <Table.HeaderCell>Languages</Table.HeaderCell>
            <Table.HeaderCell textAlign="right">Strings</Table.HeaderCell>
            <Table.HeaderCell>Active Period</Table.HeaderCell>
            <Table.HeaderCell>Joined</Table.HeaderCell>
          </Table.Row>
        </Table.Header>

        <Table.Body>
          {items.map((contributor) => {
            const contribs = contributor.contributions || [];
            const langCount = contribs.length;
            const isExpanded = expandedRows[contributor.id];

            return (
              <React.Fragment key={contributor.id}>
                <Table.Row
                  style={{ cursor: langCount > 0 ? 'pointer' : 'default' }}
                  onClick={() => langCount > 0 && toggleRow(contributor.id)}
                >
                  <Table.Cell>
                    <Icon name="user" />
                    <strong>{contributor.fullname}</strong>
                    {contributor.native_languages?.length > 0 && (
                      <div style={{ marginTop: '0.3em' }}>
                        {contributor.native_languages.map((code) => (
                          <Label key={code} size="tiny" basic>
                            {langName(code)}
                          </Label>
                        ))}
                      </div>
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    {langCount > 0 ? (
                      <>
                        <Icon name={isExpanded ? 'chevron down' : 'chevron right'} />
                        {langCount} language{langCount !== 1 ? 's' : ''}
                      </>
                    ) : (
                      <span style={{ color: '#999' }}>No contributions yet</span>
                    )}
                  </Table.Cell>
                  <Table.Cell textAlign="right">
                    {contributor.total_changes > 0
                      ? formatNumber(contributor.total_changes)
                      : '-'}
                  </Table.Cell>
                  <Table.Cell>
                    {contributor.first_activity && contributor.last_activity
                      ? `${contributor.first_activity} \u2014 ${contributor.last_activity}`
                      : '-'}
                  </Table.Cell>
                  <Table.Cell>
                    {contributor.joined_at
                      ? new Date(contributor.joined_at).toLocaleDateString('en-CA')
                      : '-'}
                  </Table.Cell>
                </Table.Row>

                {isExpanded && contribs.length > 0 && (
                  <Table.Row>
                    <Table.Cell colSpan="5" style={{ background: '#f9f9f9' }}>
                      <Table basic="very" compact size="small">
                        <Table.Header>
                          <Table.Row>
                            <Table.HeaderCell>Language</Table.HeaderCell>
                            <Table.HeaderCell textAlign="right">Strings</Table.HeaderCell>
                            <Table.HeaderCell>First</Table.HeaderCell>
                            <Table.HeaderCell>Last</Table.HeaderCell>
                          </Table.Row>
                        </Table.Header>
                        <Table.Body>
                          {contribs.map((lang) => (
                            <Table.Row key={lang.language}>
                              <Table.Cell>
                                <Label size="tiny" color="blue">
                                  {lang.language}
                                </Label>{' '}
                                {langName(lang.language)}
                              </Table.Cell>
                              <Table.Cell textAlign="right">
                                {formatNumber(lang.changes)}
                              </Table.Cell>
                              <Table.Cell>{lang.first_date}</Table.Cell>
                              <Table.Cell>{lang.last_date}</Table.Cell>
                            </Table.Row>
                          ))}
                        </Table.Body>
                      </Table>
                    </Table.Cell>
                  </Table.Row>
                )}
              </React.Fragment>
            );
          })}
        </Table.Body>
      </Table>
    </Container>
  );
};

export default ContributorShowcase;
